/**
 * Consult Scheduling — feature flag (ships DARK · plan §4).
 *
 * Mirrors the Team Inbox flag (`src/features/teaminbox/lib/flags.ts`) exactly.
 * The flag lives at Firestore `settings/flags` field `consulting`:
 *
 *   { consulting: { enabled: false } }            → OFF for everyone
 *   { consulting: { enabled: true } }             → ON for every team member
 *   { consulting: { enabled: ["<uid>", …] } }     → ON for the listed uids
 *                                                    (the dark-launch shape:
 *                                                    enabled: ["rohit"]).
 *
 * DEFAULT IS OFF: a missing doc, missing field, malformed value or read error
 * all resolve to OFF — the module is invisible until explicitly switched on,
 * and rollback is a flag flip, not a redeploy. `settings/` team-read is already
 * rules-allowed, so no firestore.rules change is needed for this flag.
 *
 * The pure resolver below is SDK-free for the Node test runner; the subscribe
 * seam lazy-imports Firebase so the pure layer never pulls the SDK into tests.
 */

/** PURE — resolve the raw `consulting` flag value for a user. Default OFF. */
export function resolveConsultingFlag(
  raw: unknown,
  uid: string | null | undefined,
): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const enabled = (raw as { enabled?: unknown }).enabled;
  if (enabled === true) return true;
  if (Array.isArray(enabled)) {
    const u = String(uid || '').trim();
    if (!u) return false;
    return enabled.some((e) => typeof e === 'string' && e === u);
  }
  return false;
}

/**
 * Per-browser local opt-in — mirrors `lib/featureFlags.ts` lensesOptIn().
 *
 * Lets a reviewer preview Door A in just their own browser WITHOUT touching the
 * shared `settings/flags` Firestore doc (parallel-safety — no other session can
 * see it). `?consult=1` turns it on (+persists to localStorage); `?consult=0`
 * clears it; otherwise the persisted localStorage choice wins. Default false
 * (dark). Safe in non-browser contexts (tests / SSR) — guards for no `window`.
 */
const CONSULTING_KEY = 'avbp.flags.consulting';

export function consultLocalOptIn(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('consult');
    if (q === '1' || q === 'true') {
      try {
        window.localStorage.setItem(CONSULTING_KEY, '1');
      } catch {
        /* private mode — query still wins this load */
      }
      return true;
    }
    if (q === '0' || q === 'false') {
      try {
        window.localStorage.setItem(CONSULTING_KEY, '0');
      } catch {
        /* ignore */
      }
      return false;
    }
    return window.localStorage.getItem(CONSULTING_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Capture the `?consult=1` / `?consult=0` query param ONCE at app startup and
 * persist it to localStorage, so the opt-in sticks no matter which route the
 * deep-link lands on. Without this, a `?consult=1` link that lands on Home and
 * then client-side-navigates to Leads loses the param before anything reads it
 * (the router rewrites the URL), so the opt-in never persists. Call this once at
 * app entry (main.tsx), before render. No-op in non-browser contexts.
 *
 * It simply runs `consultLocalOptIn()` for its read+persist side-effect; the
 * boolean result is intentionally discarded — the value is consumed later by
 * `subscribeConsultingFlag` once a lead drawer mounts.
 */
export function initConsultLocalOptIn(): void {
  void consultLocalOptIn();
}

/** Persist a per-browser consult opt-in (used by an in-shell preview toggle). */
export function setConsultLocalOptIn(on: boolean): void {
  try {
    window.localStorage.setItem(CONSULTING_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Subscribe to the flag for ONE user. Effective = (local opt-in || Firestore
 * `settings/flags.consulting`). Emits the LOCAL opt-in immediately (so a
 * `?consult=1` browser shows Door A with zero flash and no round-trip), then
 * live-updates from Firestore — once that arrives the effective value OR-folds
 * the two sources. Any Firestore error falls back to the local opt-in only, so
 * default stays OFF when neither source is set. Returns an unsubscribe fn.
 */
export function subscribeConsultingFlag(
  uid: string | null | undefined,
  cb: (on: boolean) => void,
): () => void {
  const local = consultLocalOptIn();
  cb(local);
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    try {
      const [{ db }, { doc, onSnapshot }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/firestore'),
      ]);
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, 'settings', 'flags'),
        (snap) => {
          const data = snap.exists() ? snap.data() : null;
          const remote = resolveConsultingFlag(
            data ? (data as { consulting?: unknown }).consulting : null,
            uid,
          );
          cb(local || remote);
        },
        () => cb(local),
      );
    } catch {
      /* SDK unavailable → local opt-in only (default OFF) */
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
